import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { BreadcrumbSchema, OrganizationSchema } from '@/components/SEOSchema';
import { ArrowRight, ChevronDown, Video } from 'lucide-react';
import { useCalEmbed, openCalModal } from '@/hooks/useCalEmbed';
import { Button } from '@/components/ui/button';
import Footer from '@/components/Footer';
import SeoClusterLinks from '@/components/SeoClusterLinks';
import useHreflang, { HREFLANG_SETS } from '@/hooks/useHreflang';
import { useSEO } from '@/hooks/useSEO';
import { useTracking, useScrollTracking, useSectionTracking } from '@/hooks/useTracking';
import { SeoHero } from './components/SeoHero';
import { SeoSection } from './components/SeoSection';
import { ComparisonTable } from './components/ComparisonTable';
import { TrustStrip } from './components/TrustStrip';

const COUNTRIES = [
  { code: 'de', flag: '🇩🇪', name: 'Deutschland', to: '/steuerruecklagen-freelancer-deutschland', teaser: 'Einkommensteuer, Soli, Gewerbesteuer-Freibetrag und Krankenversicherung – mit Rechner nach deutschem Steuerrecht und den Vorauszahlungsterminen am 10. März/Juni/September/Dezember.', cta: 'Steuerrücklagen in Deutschland berechnen' },
  { code: 'at', flag: '🇦🇹', name: 'Österreich', to: '/steuerruecklagen-freelancer-oesterreich', teaser: 'ESt-Stufentarif, SVS-Beiträge inklusive Nachbemessung, Gewinnfreibetrag und Kleinunternehmer-Grenze – mit Rechner für Österreich und den ESt-Terminen am 15. Februar/Mai/August/November.', cta: 'Steuerrücklagen in Österreich berechnen' },
  { code: 'ch', flag: '🇨🇭', name: 'Schweiz', to: '/steuerruecklagen-freelancer-schweiz', teaser: 'Direkte Bundessteuer, Kantonssteuer für alle 26 Kantone, AHV/IV/EO über die Ausgleichskasse und die MWST-Grenze von CHF 100\'000 – mit Kantonsauswahl im Rechner.', cta: 'Steuerrücklagen in der Schweiz planen' },
];

const ROWS = [
  { label: 'Einkommensteuer', values: { de: '14–45 % (progressiv)', at: '20–55 % (Stufentarif)', ch: 'Bund + Kanton + Gemeinde' } },
  { label: 'Umsatzsteuer / MWST', values: { de: '19 % / 7 %', at: '20 % / 10 %', ch: '8,1 % / 2,6 %' } },
  { label: 'Sozialabgaben', values: { de: 'KV/PV ~18–20 %', at: 'SVS ~27 %', ch: 'AHV/IV/EO ~10 %' } },
  { label: 'Kleinunternehmer-Grenze', values: { de: '25.000 €', at: '55.000 €', ch: 'CHF 100\'000' } },
  { label: 'Faustregel Rücklage', values: { de: '35–45 %', at: '40–50 %', ch: '25–35 %' } },
];

const FAQS = [
  { q: 'Wie viel sollte ich als Freelancer grundsätzlich zurücklegen?', a: 'Als grobe Orientierung über alle drei Länder: rund ein Drittel bis die Hälfte des Gewinns – abhängig von Land, Gewinnhöhe und Sozialversicherung. Den konkreten Prozentsatz für deine Situation liefert der Rechner auf der jeweiligen Länderseite.' },
  { q: 'Warum unterscheiden sich die Rücklagen zwischen Deutschland, Österreich und der Schweiz so stark?', a: 'Vor allem wegen der Sozialabgaben: In Österreich ist die SVS mit rund 27 % Pflicht, in Deutschland fällt für Freiberufler meist nur die Krankenversicherung an, in der Schweiz liegt die AHV bei rund 10 %. Dazu kommen unterschiedliche Steuertarife und Freibeträge.' },
  { q: 'Brauche ich ein separates Steuerkonto?', a: 'Dringend empfohlen. Überweise monatlich die berechnete Rücklage auf ein separates Konto und rühre das Geld bis zur Steuer- bzw. SVS-/AHV-Rechnung nicht an. ivoliq zeigt dir den Betrag nach jeder Rechnung automatisch.' },
];

export default function SteuerruecklagenFreelancer() {
  const [openFaq, setOpenFaq] = useState(null);
  const { trackCTAClick, trackFAQOpen } = useTracking();
  useCalEmbed();
  useScrollTracking();
  const heroRef = useRef(null); const faqRef = useRef(null); const finalCtaRef = useRef(null);
  useSectionTracking('hero', heroRef); useSectionTracking('faq', faqRef); useSectionTracking('final_cta', finalCtaRef);

  useSEO({
    title: 'Steuerrücklagen für Freelancer – Länderwahl DE, AT, CH',
    description: 'Steuerrücklagen für Freelancer in Deutschland, Österreich und der Schweiz: Wähle dein Land und berechne, wie viel du für Steuern zurücklegen musst.',
    canonical: 'https://ivoliq.io/steuerruecklagen-freelancer',
    robots: 'index,follow',
  });
  useHreflang({ currentUrl: HREFLANG_SETS.steuerruecklagen.xDefault, alternates: HREFLANG_SETS.steuerruecklagen, xDefault: HREFLANG_SETS.steuerruecklagen.xDefault });

  const faqSchema = { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: FAQS.map(f => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })) };
  const toggleFaq = (i, q) => { setOpenFaq(openFaq === i ? null : i); if (openFaq !== i) trackFAQOpen(q); };

  return (
    <div className="min-h-screen bg-white">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <BreadcrumbSchema items={[{ name: 'Home', url: '/' }, { name: 'Steuerrücklagen für Freelancer' }]} />
      <OrganizationSchema />

      <SeoHero heroRef={heroRef} eyebrow="Steuerrücklagen für Freelancer" title="Steuerrücklagen für Freelancer:" titleAccent="Wähle dein Land"
        subtitle="Deutschland, Österreich oder Schweiz – die Regeln für Einkommensteuer und Sozialabgaben unterscheiden sich stark. Deshalb gibt es für jedes Land einen eigenen Rechner mit den passenden Sätzen, Freibeträgen und Fristen."
        trust={<TrustStrip />}
        mediaLabel="Länder-Guides"
        media={
          <ul className="space-y-3" data-testid="hub-country-teasers">
            {COUNTRIES.map(c => (
              <li key={c.code}>
                <Link to={c.to} data-testid={`hub-teaser-${c.code}`} onClick={() => trackCTAClick('hub', c.name)} className="group flex items-center gap-4 rounded-xl border border-gray-200 p-4 transition-colors hover:border-emerald-300 hover:bg-emerald-50/50">
                  <span className="text-2xl" aria-hidden="true">{c.flag}</span>
                  <span className="flex-1"><span className="block font-semibold text-gray-900">{c.name}</span><span className="block text-xs text-gray-500">{c.cta}</span></span>
                  <ArrowRight className="h-4 w-4 text-emerald-600 transition-transform group-hover:translate-x-1" />
                </Link>
              </li>
            ))}
          </ul>
        }
      />

      <SeoSection id="laender" variant="soft" title="Was dich in deinem Land erwartet" testId="how-much-section">
        <div className="grid gap-6 md:grid-cols-3">
          {COUNTRIES.map(c => (
            <article key={c.code} className="rounded-2xl bg-white p-6 ring-1 ring-gray-200">
              <p className="text-3xl" aria-hidden="true">{c.flag}</p>
              <h3 className="mt-3 text-lg font-semibold text-gray-900">Steuerrücklagen in {c.code === 'ch' ? 'der ' : ''}{c.name}</h3>
              <p className="mt-2 text-sm text-gray-600">{c.teaser}</p>
              <Link to={c.to} className="mt-4 inline-flex items-center gap-1.5 font-semibold text-emerald-700 underline-offset-4 hover:underline">{c.cta} <ArrowRight className="h-4 w-4" /></Link>
            </article>
          ))}
        </div>
        <div className="mt-10">
          <ComparisonTable testId="hub-comparison-table" caption="Eckwerte Steuern und Sozialabgaben DE/AT/CH"
            columns={[{ key: 'de', label: 'Deutschland' }, { key: 'at', label: 'Österreich' }, { key: 'ch', label: 'Schweiz' }]}
            rows={ROWS}
            footnote="Grobe Eckwerte zum Vergleich – die konkreten Prozentsätze, Rechner und Fristen findest du in den Länder-Guides." />
        </div>
        <p className="mt-8 max-w-3xl text-base md:text-lg leading-relaxed text-gray-700">
          Egal in welchem Land: Der häufigste Fehler ist, die Rücklage nur an der Einkommensteuer zu bemessen und Sozialabgaben zu vergessen – die SVS-Nachbemessung in Österreich, die volle Krankenversicherung in Deutschland oder die definitive AHV-Abrechnung in der Schweiz kommen oft Jahre später und auf einmal. ivoliq berechnet nach jeder Rechnung, was du wirklich zurücklegen musst.
        </p>
      </SeoSection>

      <SeoSection id="faq" title="Häufige Fragen" sectionRef={faqRef} testId="faq-section" width="max-w-3xl">
        <div className="space-y-3">
          {FAQS.map((f, i) => (
            <div key={i} className="overflow-hidden rounded-xl bg-white ring-1 ring-gray-200" data-testid={`faq-item-${i}`}>
              <button type="button" onClick={() => toggleFaq(i, f.q)} aria-expanded={openFaq === i} className="flex w-full items-center justify-between px-5 py-4 text-left transition-colors hover:bg-gray-50" data-testid={`faq-toggle-${i}`}>
                <span className="pr-4 font-semibold text-gray-900">{f.q}</span>
                <ChevronDown className={`h-5 w-5 flex-none text-gray-500 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
              </button>
              <div className={`px-5 pb-4 text-gray-600 leading-relaxed ${openFaq === i ? '' : 'hidden'}`}>{f.a}</div>
            </div>
          ))}
        </div>
      </SeoSection>

      <SeoSection variant="accent" sectionRef={finalCtaRef} testId="final-cta-section">
        <div className="max-w-3xl">
          <h2 className="font-display text-3xl sm:text-4xl font-bold">Nie wieder Steuerschock – in DE, AT und CH</h2>
          <p className="mt-4 text-base md:text-lg text-emerald-100/80">ivoliq berechnet deine Rücklage automatisch aus echten Rechnungen und Ausgaben – nach dem Steuerrecht deines Landes.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link to="/signup" onClick={() => trackCTAClick('final_cta', 'Jetzt kostenlos starten')} data-testid="final-cta-button"><Button size="lg" className="w-full bg-white text-[#062417] hover:bg-emerald-50 h-12 px-8 text-base font-semibold">Jetzt kostenlos starten</Button></Link>
            <Button size="lg" variant="outline" onClick={openCalModal} className="border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white h-12 px-8 text-base" data-testid="final-demo-button"><Video className="h-5 w-5 mr-2" /> Demo buchen</Button>
          </div>
          <div className="mt-8"><TrustStrip dark /></div>
        </div>
      </SeoSection>

      <SeoClusterLinks cluster="steuerruecklagen" country={null} />
      <Footer />
    </div>
  );
}
